/**
 * Jovii — DOWNLOADS: the Origin Private File System, behind one small door.
 *
 * OPFS and not IndexedDB, and not the Cache API:
 *
 *   - **IndexedDB** would hold a 12 GB film as one Blob value, and reading a
 *     byte range out of it means `blob.slice()` on a value the implementation
 *     may have materialised whole. It is also the storage every browser evicts
 *     first under pressure.
 *   - **The Cache API** is a response store, keyed by request, and reading a
 *     range out of a cached 12 GB response is the same problem with a service
 *     worker attached — and `index.html` says why there is no service worker.
 *   - **OPFS** is a real file: `createWritable()` appends, `getFile()` gives a
 *     `File`, and `File.slice(start, end).arrayBuffer()` is a genuine range read
 *     that never materialises the rest. That last property is the whole reason
 *     the player can demux out of a download without a second pipeline.
 *
 * EVERY FUNCTION HERE ANSWERS `null` RATHER THAN THROWING when the platform does
 * not have OPFS (older Safari, a browser in a mode that refuses storage). The
 * screens above check one boolean, `supported()`, and otherwise never learn.
 */

import { fileNameFor } from './index-state';
import type { RemuxSink } from './remux';
import type { LocalByteSource } from './types';

/** The directory every download lives in, under the origin's private root. */
const DIR = 'downloads';

export function supported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.storage?.getDirectory === 'function' &&
    typeof FileSystemFileHandle !== 'undefined'
  );
}

async function dir(create = false): Promise<FileSystemDirectoryHandle | null> {
  if (!supported()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    return await root.getDirectoryHandle(DIR, { create });
  } catch {
    // NotFoundError before anything has ever been downloaded is the normal case.
    return null;
  }
}

async function fileHandle(key: string, create = false): Promise<FileSystemFileHandle | null> {
  const d = await dir(create);
  if (!d) return null;
  try {
    return await d.getFileHandle(fileNameFor(key), { create });
  } catch {
    return null;
  }
}

/**
 * ASK THE ORIGIN TO BE PERSISTENT, ONCE.
 *
 * `persist()` moves this origin's storage out of the "best effort" bucket, which
 * is what stops a browser evicting a half-downloaded film to make room for
 * someone's analytics. It is a request, not a setting: Chrome grants it on
 * engagement signals, Firefox prompts, and WebKit resolves it without changing
 * the 7-day eviction rule at all — which is precisely why the interface tells
 * people to keep Jovii on the Home Screen rather than relying on this returning
 * true. Called once from the manager; the result is advisory.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (typeof navigator?.storage?.persisted === 'function' && (await navigator.storage.persisted())) return true;
    if (typeof navigator?.storage?.persist !== 'function') return false;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/** `{quota, usage}` or null when the browser will not say. */
export async function estimate(): Promise<{ quota?: number; usage?: number } | null> {
  try {
    if (typeof navigator?.storage?.estimate !== 'function') return null;
    return await navigator.storage.estimate();
  } catch {
    return null;
  }
}

/**
 * How many bytes of this key are already on disk, or null when there is no file.
 *
 * THE AUTHORITY FOR EVERY RESUME. See the header of `index-state.ts`: the index
 * is a cache of this number and is reconciled to it, never the other way round.
 */
export async function fileBytes(key: string): Promise<number | null> {
  const handle = await fileHandle(key);
  if (!handle) return null;
  try {
    return (await handle.getFile()).size;
  } catch {
    return null;
  }
}

/**
 * A writer positioned at `at`, ready to append.
 *
 * `keepExistingData: true` plus an explicit seek is what makes a resume a resume:
 * without the flag `createWritable()` truncates, and the file that held 400 MB
 * five seconds ago holds nothing. The handle is returned rather than wrapped in
 * a helper that writes one chunk, because opening a writable per 4 MB chunk on a
 * 12 GB film is three thousand opens.
 */
export async function openAppend(key: string, at: number): Promise<FileSystemWritableFileStream | null> {
  const handle = await fileHandle(key, true);
  if (!handle) return null;
  try {
    const writable = await handle.createWritable({ keepExistingData: true });
    if (at > 0) await writable.seek(at);
    else await writable.truncate(0);
    return writable;
  } catch {
    return null;
  }
}

export async function removeFile(key: string): Promise<void> {
  const d = await dir();
  if (!d) return;
  try {
    await d.removeEntry(fileNameFor(key));
  } catch {
    /* already gone, which is the state the caller wanted */
  }
}

/** Every download file, gone. The "Delete all" in the Downloads menu. */
export async function removeAll(): Promise<void> {
  const root = await (async () => {
    try {
      return supported() ? await navigator.storage.getDirectory() : null;
    } catch {
      return null;
    }
  })();
  if (!root) return;
  try {
    await root.removeEntry(DIR, { recursive: true });
  } catch {
    /* nothing there */
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE REMUX SINKS — where a converted file's bytes are actually written
 *
 * Both of these run INSIDE `remux.worker.ts`, which is the whole reason the
 * remuxer is in a worker at all. `createSyncAccessHandle()` is a plain
 * positioned read/write on a real file and exists ONLY in a worker context;
 * against a 12 GB film it is not a marginal win over `createWritable()`'s
 * promise-per-write stream, and it is what makes the final seek back to offset
 * 0 free rather than a rewrite of everything after it.
 *
 * The interface is the same for both so the remuxer never learns which one it
 * has (see `RemuxSink` in remux.ts). The methods are async on both sides even
 * though the OPFS one is synchronous underneath: the folder one genuinely is
 * not, and a union of two shapes would be a branch in every write.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The OPFS staging file, through a sync access handle.
 *
 * TRUNCATED TO ZERO ON OPEN, always. A remux is not resumable — a half-written
 * output is not a prefix of the finished one, because its header carries no
 * duration and its length says nothing about which source byte produced it — so
 * whatever an abandoned earlier attempt left here is not a resume offset, it is
 * rubbish sitting exactly where this file's header is about to go.
 *
 * `flush()` before `close()` is not optional: without it the last fragments and
 * the patched header can still be inside the implementation's buffer when the
 * manager asks the file how long it is, and a download reports itself short.
 */
export async function openRemuxSink(key: string): Promise<RemuxSink> {
  const root = await navigator.storage.getDirectory();
  const folder = await root.getDirectoryHandle(DIR, { create: true });
  const handle = await folder.getFileHandle(fileNameFor(key), { create: true });
  const access = await handle.createSyncAccessHandle();
  access.truncate(0);
  let at = 0;
  return {
    async write(bytes: Uint8Array, position?: number): Promise<void> {
      const target = position ?? at;
      access.write(bytes as unknown as BufferSource, { at: target });
      at = target + bytes.byteLength;
    },
    async seek(position: number): Promise<void> {
      at = position;
    },
    async truncate(n: number): Promise<void> {
      access.truncate(n);
      if (at > n) at = n;
    },
    async close(): Promise<void> {
      access.flush();
      access.close();
    }
  };
}

/**
 * The reader's own folder, through a writable stream.
 *
 * `createSyncAccessHandle()` is OPFS-only — a handle from `showDirectoryPicker`
 * does not have one, and there is no way to get one for a file outside the
 * origin's private storage. A `FileSystemWritableFileStream` does support
 * positioned writes and a seek, which is all the header patch needs.
 *
 * `keepExistingData: false` because a remux starts from zero every time; the
 * previous attempt's bytes are not a prefix of this one's.
 */
export async function openFolderRemuxSink(handle: FileSystemFileHandle): Promise<RemuxSink> {
  const stream = await handle.createWritable({ keepExistingData: false });
  let at = 0;
  return {
    async write(bytes: Uint8Array, position?: number): Promise<void> {
      const target = position ?? at;
      await stream.write({ type: 'write', position: target, data: bytes as Uint8Array<ArrayBuffer> });
      at = target + bytes.byteLength;
    },
    async seek(position: number): Promise<void> {
      await stream.seek(position);
      at = position;
    },
    async truncate(n: number): Promise<void> {
      await stream.truncate(n);
      if (at > n) at = n;
    },
    async close(): Promise<void> {
      await stream.close();
    }
  };
}

/**
 * A COMPLETED download as a byte source the player can read.
 *
 * `File.slice()` is the whole implementation and it is the right one: a `File`
 * from OPFS is a lazy handle onto the file, so slicing 2 MB out of the middle of
 * a 12 GB download reads 2 MB. The `File` is fetched ONCE and kept, because
 * `getFile()` is itself a round trip through the file system and the demuxer
 * asks for a chunk every few hundred milliseconds.
 *
 * Returns null when there is no file, or when the file is shorter than the size
 * the index claims — a truncated download must fall back to the network rather
 * than feed the demuxer a file that stops mid-cluster.
 */
export async function localSource(key: string, expectedSize: number): Promise<LocalByteSource | null> {
  const handle = await fileHandle(key);
  if (!handle) return null;
  let file: File;
  try {
    file = await handle.getFile();
  } catch {
    return null;
  }
  if (expectedSize > 0 && file.size < expectedSize) return null;
  const size = expectedSize > 0 ? expectedSize : file.size;
  return {
    size,
    async read(start: number, end: number, signal?: AbortSignal): Promise<Uint8Array> {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const from = Math.max(0, Math.min(start, size));
      const to = Math.max(from, Math.min(end, size));
      if (to === from) return new Uint8Array(0);
      const buffer = await file.slice(from, to).arrayBuffer();
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      return new Uint8Array(buffer);
    }
  };
}

/* ── THE POSTER, KEPT WITH THE FILM ─────────────────────────────────────────
 *
 * The owner, offline, on the Downloads screen: "there are no assets for the
 * tile." The index stored the poster's URL, and a URL is nothing on a plane.
 * So the poster is written beside the film as `<key>.poster` when a download
 * is enqueued, and the screen asks for a blob URL and gets the cached one
 * first, the network one second. THE BYTES ARRIVE FROM OUTSIDE THIS MODULE:
 * lib/ui/poster-bytes.ts fetches the catalogue image, because this module is
 * gated to never fetch anything (client-only.test.ts) so that media bytes can
 * only ever travel through RangeReader. A poster that will not fetch is not
 * an error: the row draws its initial, as it did.
 */

export async function savePoster(key: string, bytes: Uint8Array): Promise<void> {
  try {
    const folder = await dir(true);
    if (!folder) return;
    const handle = await folder.getFileHandle(`${key}.poster`, { create: true });
    const writable = await handle.createWritable();
    await writable.write(new Blob([bytes as BlobPart]));
    await writable.close();
  } catch {
    /* no poster on the disk; the row keeps its initial */
  }
}

export async function posterUrlFor(key: string): Promise<string | null> {
  try {
    const folder = await dir(false);
    if (!folder) return null;
    const handle = await folder.getFileHandle(`${key}.poster`);
    const file = await handle.getFile();
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

export async function removePoster(key: string): Promise<void> {
  try {
    const folder = await dir(false);
    await folder?.removeEntry(`${key}.poster`);
  } catch {
    /* already gone */
  }
}

/**
 * Open the real destination file on the 'folder' strategy.
 *
 * Bytes go straight into the reader's own folder — no staging copy, no browser
 * quota, and a 12 GB film is no different from a 700 MB one. `keepExisting`
 * carries a resume: the stream is opened at the end of what is already there.
 */
export async function openInFolder(
  folder: FileSystemDirectoryHandle,
  name: string,
  at: number
): Promise<{ stream: FileSystemWritableFileStream; existing: number } | null> {
  try {
    const handle = await folder.getFileHandle(name, { create: true });
    const existing = at > 0 ? (await handle.getFile()).size : 0;
    const from = Math.min(at, existing);
    const stream = await handle.createWritable({ keepExistingData: from > 0 });
    if (from > 0) await stream.seek(from);
    return { stream, existing: from };
  } catch {
    return null;
  }
}

/**
 * The destination file's own handle, for the remux worker.
 *
 * `FileSystemFileHandle` is structured-cloneable, so the page opens (or
 * creates) the file here and posts the handle across; the worker does the
 * writing. That is the only way the folder strategy can be served from a
 * worker at all — there is no key it could look the file up by, the way OPFS
 * has one.
 */
export async function folderFileHandle(
  folder: FileSystemDirectoryHandle,
  name: string
): Promise<FileSystemFileHandle | null> {
  try {
    return await folder.getFileHandle(name, { create: true });
  } catch {
    return null;
  }
}

/** How many bytes are already in the destination file, for a resume. */
export async function folderFileBytes(
  folder: FileSystemDirectoryHandle,
  name: string
): Promise<number> {
  try {
    const handle = await folder.getFileHandle(name);
    return (await handle.getFile()).size;
  } catch {
    return 0;
  }
}

/** The staged file itself, so the 'save' strategy can hand it to the browser. */
export async function stagedFile(key: string): Promise<File | null> {
  const handle = await fileHandle(key);
  if (!handle) return null;
  try {
    return await handle.getFile();
  } catch {
    return null;
  }
}
